/**
 * Diagramas de la landing: explican, no decoran.
 *
 * Tres cosas de ÁGORA no se entienden con texto solo y son justamente las que
 * deciden una venta: que las tres superficies están vivas a la vez, que una
 * afirmación del documento se puede seguir hasta la persona que la dijo, y que
 * una institución tiene su propio espacio separado del de las demás.
 *
 * Todo va en SVG inline con animación CSS (nada de librerías) y se detiene con
 * `prefers-reduced-motion`. Los colores salen de los tokens de marca.
 */

const CSS_ANIM = `
@keyframes viaja { from { offset-distance: 0%; opacity: 0 } 12% { opacity: 1 }
                   88% { opacity: 1 } to { offset-distance: 100%; opacity: 0 } }
@keyframes latir { 0%,100% { opacity: .25 } 50% { opacity: .9 } }
@keyframes trazo { from { stroke-dashoffset: var(--largo) } to { stroke-dashoffset: 0 } }
@keyframes aparece { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
@media (prefers-reduced-motion: reduce) {
  .anim-viaja, .anim-latir, .anim-trazo, .anim-aparece { animation: none !important }
  .anim-trazo { stroke-dashoffset: 0 !important }
  .anim-viaja { opacity: .8 !important }
}
`;

/** Un punto de datos que recorre un camino: es el aporte viajando en vivo. */
function Pulso({ path, delay, color }: { path: string; delay: number; color: string }) {
  return (
    <circle
      r="4"
      fill={color}
      className="anim-viaja"
      style={{
        offsetPath: `path("${path}")`,
        animation: `viaja 2.6s ${delay}s cubic-bezier(.4,0,.2,1) infinite`,
      }}
    />
  );
}

/* ── 1 · Las tres superficies, sincronizadas ─────────────────────────────── */

export function DiagramaSuperficies() {
  const aCabina = 'M 120 150 C 200 150, 220 90, 300 90';
  const aPantalla = 'M 420 90 C 500 90, 520 150, 600 150';

  return (
    <figure className="rounded-2xl border border-border bg-surface-raised p-5">
      <style>{CSS_ANIM}</style>
      <svg viewBox="0 0 720 240" className="w-full" role="img"
        aria-label="El teléfono del participante, la Cabina de quien facilita y la Pantalla de la sala, sincronizados en tiempo real">
        <defs>
          <linearGradient id="gd-marca" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--color-primary-600)" />
            <stop offset="52%" stopColor="var(--color-cyan-500)" />
            <stop offset="100%" stopColor="var(--color-violet-500)" />
          </linearGradient>
        </defs>

        {/* caminos */}
        <path d={aCabina} fill="none" stroke="var(--border-default)" strokeWidth="2" strokeDasharray="4 5" />
        <path d={aPantalla} fill="none" stroke="var(--border-default)" strokeWidth="2" strokeDasharray="4 5" />
        <Pulso path={aCabina} delay={0} color="var(--color-primary-600)" />
        <Pulso path={aCabina} delay={1.3} color="var(--color-cyan-500)" />
        <Pulso path={aPantalla} delay={0.7} color="var(--color-cyan-500)" />

        {/* teléfono */}
        <g>
          <rect x="40" y="96" width="70" height="112" rx="10" fill="var(--surface-base)" stroke="var(--border-strong)" strokeWidth="2" />
          <rect x="50" y="112" width="50" height="8" rx="4" fill="var(--color-primary-600)" opacity=".8" />
          <rect x="50" y="128" width="38" height="6" rx="3" fill="var(--border-strong)" />
          <rect x="50" y="142" width="46" height="6" rx="3" fill="var(--border-strong)" />
          <circle cx="75" cy="180" r="14" fill="url(#gd-marca)" className="anim-latir"
            style={{ animation: 'latir 2.2s ease-in-out infinite' }} />
          <text x="75" y="228" textAnchor="middle" className="fill-content" fontSize="13" fontWeight="600">Bolsillo</text>
        </g>

        {/* cabina */}
        <g>
          <rect x="300" y="40" width="120" height="86" rx="8" fill="var(--surface-base)" stroke="var(--border-strong)" strokeWidth="2" />
          <rect x="312" y="54" width="44" height="6" rx="3" fill="var(--color-primary-600)" opacity=".8" />
          <rect x="312" y="68" width="96" height="5" rx="2.5" fill="var(--border-strong)" />
          <rect x="312" y="80" width="80" height="5" rx="2.5" fill="var(--border-strong)" />
          <rect x="312" y="92" width="88" height="5" rx="2.5" fill="var(--border-strong)" />
          <rect x="344" y="126" width="32" height="6" rx="3" fill="var(--border-strong)" />
          <text x="360" y="152" textAnchor="middle" className="fill-content" fontSize="13" fontWeight="600">Cabina</text>
        </g>

        {/* pantalla */}
        <g>
          <rect x="600" y="86" width="86" height="66" rx="6" fill="var(--color-navy-900)" stroke="var(--border-strong)" strokeWidth="2" />
          <rect x="612" y="100" width="36" height="7" rx="3.5" fill="var(--color-cyan-500)" />
          <rect x="612" y="114" width="60" height="5" rx="2.5" fill="#ffffff" opacity=".35" />
          <rect x="612" y="126" width="48" height="5" rx="2.5" fill="#ffffff" opacity=".35" />
          <rect x="636" y="152" width="14" height="12" fill="var(--border-strong)" />
          <rect x="618" y="164" width="50" height="5" rx="2.5" fill="var(--border-strong)" />
          <text x="643" y="192" textAnchor="middle" className="fill-content" fontSize="13" fontWeight="600">Pantalla</text>
        </g>
      </svg>
      <figcaption className="mt-3 font-sans text-sm leading-relaxed text-content-muted">
        <strong className="text-content">Las tres a la vez.</strong> Lo que alguien
        aporta desde su teléfono aparece en la Cabina de quien facilita y, si así
        se decide, se proyecta en la Pantalla que ve la sala. Sin recargar, sin
        pasar listas, sin dictar.
      </figcaption>
    </figure>
  );
}

/* ── 2 · De la afirmación al aporte (la trazabilidad) ────────────────────── */

export function DiagramaTrazabilidad() {
  return (
    <figure className="rounded-2xl border border-border bg-surface-raised p-5">
      <style>{CSS_ANIM}</style>
      <svg viewBox="0 0 720 250" className="w-full" role="img"
        aria-label="Una afirmación del documento final se sigue hasta el aporte que la respalda, con su autor y su hora">
        {/* documento */}
        <g>
          <rect x="30" y="30" width="180" height="190" rx="10" fill="var(--surface-base)" stroke="var(--border-strong)" strokeWidth="2" />
          <rect x="48" y="52" width="90" height="8" rx="4" fill="var(--color-navy-900)" opacity=".85" />
          <rect x="48" y="76" width="144" height="5" rx="2.5" fill="var(--border-strong)" />
          <rect x="48" y="88" width="132" height="5" rx="2.5" fill="var(--border-strong)" />
          {/* la afirmación citada, resaltada */}
          <rect x="42" y="106" width="156" height="34" rx="6" fill="var(--color-primary-600)" opacity=".10" />
          <rect x="48" y="114" width="120" height="5" rx="2.5" fill="var(--color-primary-600)" />
          <rect x="48" y="126" width="96" height="5" rx="2.5" fill="var(--color-primary-600)" />
          <rect x="48" y="156" width="140" height="5" rx="2.5" fill="var(--border-strong)" />
          <rect x="48" y="168" width="118" height="5" rx="2.5" fill="var(--border-strong)" />
          <text x="120" y="208" textAnchor="middle" className="fill-content-muted" fontSize="12">Documento final</text>
        </g>

        {/* el hilo de la cita */}
        <path
          id="hilo" d="M 210 124 C 300 124, 320 96, 420 96"
          fill="none" stroke="var(--color-primary-600)" strokeWidth="2.5" strokeLinecap="round"
          className="anim-trazo"
          style={{ ['--largo' as string]: '260', strokeDasharray: 260, animation: 'trazo 1.6s ease-out .3s both' }}
        />
        <text x="300" y="112" textAnchor="middle" className="fill-content-muted" fontSize="11">cita</text>

        {/* el aporte */}
        <g className="anim-aparece" style={{ animation: 'aparece .5s ease-out 1.6s both' }}>
          <rect x="420" y="58" width="264" height="76" rx="10" fill="var(--surface-base)" stroke="var(--color-primary-600)" strokeWidth="2" />
          <circle cx="450" cy="80" r="11" fill="var(--color-cyan-500)" opacity=".9" />
          <rect x="470" y="74" width="86" height="6" rx="3" fill="var(--color-navy-900)" opacity=".8" />
          <rect x="470" y="86" width="60" height="5" rx="2.5" fill="var(--border-strong)" />
          <rect x="438" y="106" width="220" height="5" rx="2.5" fill="var(--border-strong)" />
          <rect x="438" y="118" width="180" height="5" rx="2.5" fill="var(--border-strong)" />
          <text x="552" y="152" textAnchor="middle" className="fill-content-muted" fontSize="12">
            El aporte: quién lo dijo, cuándo y en qué actividad
          </text>
        </g>

        {/* lo no respaldado, marcado */}
        <g className="anim-aparece" style={{ animation: 'aparece .5s ease-out 2.1s both' }}>
          <rect x="420" y="176" width="264" height="44" rx="10" fill="var(--surface-base)" stroke="var(--color-warning)" strokeWidth="2" strokeDasharray="6 4" />
          <text x="552" y="196" textAnchor="middle" className="fill-content" fontSize="12" fontWeight="600">Sin respaldo</text>
          <text x="552" y="212" textAnchor="middle" className="fill-content-muted" fontSize="11">se marca, no se disimula</text>
        </g>
      </svg>
      <figcaption className="mt-3 font-sans text-sm leading-relaxed text-content-muted">
        <strong className="text-content">Cada afirmación tira de un hilo.</strong>{' '}
        Del documento final se llega al aporte que lo sostiene, con su autor y su
        hora. Y lo que ninguna evidencia respalda queda marcado en vez de
        disimulado — eso es lo que convierte un informe opinable en un
        expediente que aguanta una pregunta incómoda.
      </figcaption>
    </figure>
  );
}

/* ── 3 · Cómo se instala en una institución ──────────────────────────────── */

export function DiagramaTenant() {
  return (
    <figure className="rounded-2xl border border-border bg-surface-raised p-5">
      <style>{CSS_ANIM}</style>
      <svg viewBox="0 0 720 220" className="w-full" role="img"
        aria-label="Cada institución tiene su propio espacio con su identidad, su gente y su evidencia separada">
        {[
          { x: 20, nombre: 'Tu institución', propio: true },
          { x: 258, nombre: 'Otra organización', propio: false },
          { x: 496, nombre: 'Un consultor', propio: false },
        ].map((t, i) => (
          <g key={t.nombre} className="anim-aparece" style={{ animation: `aparece .5s ease-out ${i * 0.15}s both` }}>
            <rect
              x={t.x} y="24" width="204" height="150" rx="12"
              fill="var(--surface-base)"
              stroke={t.propio ? 'var(--color-primary-600)' : 'var(--border-default)'}
              strokeWidth={t.propio ? 2.5 : 1.5}
            />
            {/* su identidad */}
            <circle cx={t.x + 34} cy="56" r="13"
              fill={t.propio ? 'var(--color-primary-600)' : 'var(--border-strong)'} opacity={t.propio ? 0.9 : 0.5} />
            <rect x={t.x + 56} y="50" width="70" height="6" rx="3" fill="var(--border-strong)" />
            <rect x={t.x + 56} y="62" width="48" height="5" rx="2.5" fill="var(--border-default)" />
            {/* su gente */}
            {[0, 1, 2, 3].map((k) => (
              <circle key={k} cx={t.x + 34 + k * 22} cy="100" r="8"
                fill={t.propio ? 'var(--color-cyan-500)' : 'var(--border-strong)'} opacity={t.propio ? 0.85 : 0.45} />
            ))}
            {/* su evidencia */}
            <rect x={t.x + 24} y="126" width="156" height="7" rx="3.5" fill="var(--border-strong)" opacity=".8" />
            <rect x={t.x + 24} y="139" width="120" height="7" rx="3.5" fill="var(--border-strong)" opacity=".55" />
            <text x={t.x + 102} y="196" textAnchor="middle"
              className={t.propio ? 'fill-content' : 'fill-content-muted'} fontSize="13"
              fontWeight={t.propio ? 700 : 400}>
              {t.nombre}
            </text>
          </g>
        ))}
      </svg>
      <figcaption className="mt-3 font-sans text-sm leading-relaxed text-content-muted">
        <strong className="text-content">Tu espacio, no el nuestro.</strong> Con un
        plan de organización, tu gente entra por una dirección con tu logo, las
        constancias salen con tu escudo y el de tu cooperante, y tu evidencia vive
        separada de la de cualquier otra organización. Vos administrás a tu equipo
        y tus permisos, sin pasar por nosotros.
      </figcaption>
    </figure>
  );
}
